"use client";

import type { HomePageHero, ImageContent } from "@homeinstead/schemas";
import type { PortableTextBlock } from "next-sanity";
import type { LeadSource } from "~www/types/forms/care-seeker-shallow-lead-form";
import type { PageBuilderOffice } from "~www/types/office";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";
import CareGiverLeadForm from "~www/components/LeadForm/CareGiver/CareGiverLeadForm"; // Note that the displayed step and the actual step is off by 1 because the first step is hidden.
import {
  handleDrawerClose,
  LeadFormDrawer,
} from "~www/components/LeadForm/CareGiver/LeadFormDrawer";
import {
  CareSeekerLocalLeadForm,
  HeroImage,
} from "~www/components/LeadForm/CareSeeker";
import { DEFAULT_FRANCHISE_HOME_LEAD_SOURCE } from "~www/components/LeadForm/CareSeeker/lead-source-values";
import { useMultiStepForm } from "~www/hooks/useMultiStepForm";

// Note that the displayed step and the actual step is off by 1 because the first step is hidden.
// Also, the final step is a Thank You page.
const INITIAL_CARE_SEEKER_STEP = 2;

// TODO: Find a more maintainable way to determine the amount of steps for each variant
// https://joinhonor.atlassian.net/browse/DEX-1599
const FINAL_CARE_SEEKER_STEP_BY_VARIANT: Record<
  NonNullable<HomePageHero["leadFormVariant"]>,
  number
> = {
  v1: 5,
  v2: 6, // one extra step for urgency
  v4: 7, // same as v2, one extra step for location
  v5: 7, // same as v2, one extra step for splitting PII
};

export interface FranchiseHomePageHeroProps {
  office: PageBuilderOffice;
  heroHeading: PortableTextBlock[];
  leadFormVariant?: HomePageHero["leadFormVariant"];
  placeholders?: Record<string, string>;
  heroImageLarge?: ImageContent;
  heroImageSmall?: ImageContent;
  variant?: HomePageHero["variant"];
  leadSource?: LeadSource;
}

export const FranchiseHomePageHero = ({
  office,
  heroHeading,
  leadFormVariant = "v1",
  placeholders,
  heroImageLarge,
  heroImageSmall,
  variant = "default",
  leadSource = DEFAULT_FRANCHISE_HOME_LEAD_SOURCE,
}: FranchiseHomePageHeroProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const closeCareGiverDrawer = useCallback(() => {
    router.push(handleDrawerClose({ pathname, searchParams }));
  }, [pathname, router, searchParams]);

  // Use the client search params for checking lead variant
  const careGiverForm = useMultiStepForm({
    finalStepIndex: 2,
    formId: "care-giver-form",
    initialStepIndex: 2,
    onFirst: closeCareGiverDrawer,
  });

  const isCareGiverDrawerOpen = searchParams.get("leadVariant") === "CareGiver";
  const finalStep =
    FINAL_CARE_SEEKER_STEP_BY_VARIANT[leadFormVariant] ??
    FINAL_CARE_SEEKER_STEP_BY_VARIANT.v1;

  // TODO: Move this to the lead form component itself when we move the leadform to its own package
  // https://joinhonor.atlassian.net/browse/DEX-368
  const careSeekerShallowForm = useMultiStepForm({
    finalStepIndex: finalStep,
    formId: "care-seeker-form",
    initialStepIndex: INITIAL_CARE_SEEKER_STEP,
  });

  // const { isDebug } = useIsDebug();
  const { isDebug } = { isDebug: true };

  if (!office) {
    // TODO: LOG ERROR
    return null;
  }

  const { officeID, locationName, address, officeRating, phone } = office;

  const { location } = {
    location: { name: locationName, postalCode: address.postalCode },
  };

  return (
    <>
      <div className="flex flex-col items-center overflow-hidden xl:flex-row xl:items-start">
        <CareSeekerLocalLeadForm
          className="px-4 pb-8 sm:px-10 lg:px-[72px] xl:w-full 2xl:px-[152px]"
          currentStep={careSeekerShallowForm.currentStepIndex}
          finalStep={finalStep}
          franchiseId={Number(officeID)}
          handleBack={careSeekerShallowForm.goToPreviousStep}
          handleNext={careSeekerShallowForm.goToNextStep}
          heroHeading={heroHeading}
          isDebug={isDebug}
          leadSource={leadSource}
          location={location}
          officeRating={officeRating}
          phoneNumber={phone}
          placeholders={placeholders}
          variant={variant}
          version={leadFormVariant ?? "v1"}
        />

        <HeroImage
          heroImageLarge={heroImageLarge}
          heroImageSmall={heroImageSmall}
          showLargeViewportImage={
            // On large viewports, the hero image should only be displayed on the initial lead form step.
            careSeekerShallowForm.currentStepIndex === INITIAL_CARE_SEEKER_STEP
          }
        />
      </div>

      <LeadFormDrawer
        description="Complete these steps to find care."
        hasBackButton
        isDrawerOpen={isCareGiverDrawerOpen} // Use careGiverForm's prevStep for the drawer's back button
        onBack={careGiverForm.goToPreviousStep}
        onClose={closeCareGiverDrawer} // This could also come from context/pageData if dynamic
        title="Get Care for a Loved One" // Ditto
      >
        {isCareGiverDrawerOpen && (
          <CareGiverLeadForm
            handleNext={careGiverForm.goToNextStep}
            isLastStep={careGiverForm.isLastStep}
            office={office}
          />
        )}
      </LeadFormDrawer>
    </>
  );
};
